﻿import { withAITracking } from '@microsoft/applicationinsights-react-js';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import {
	GridActionsCellItem,
	GridColDef,
	GridInitialState,
	GridRowParams,
} from '@mui/x-data-grid-pro';
import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { useProductCenterApi } from 'api/ApplicationApi';
import {
	getHealthCheckListQuery,
	healthCheckKeys,
} from 'api/queries/HealthCheckQueries';
import { HealthCheckListDto, ProtocolStatus } from 'api/v1/data-contracts';
import { Trash } from 'assets/volvoIcons';
import { AreaName } from 'domain/constants';
import { DataGridToolbar } from 'library/components/controls/datagrid/DataGridToolbar';
import { VolvoDataGrid } from 'library/components/controls/datagrid/VolvoDataGrid';
import { CustomSeverityDialog } from 'library/components/controls/dialogs/CustomSeverityDialog';
import { SectionTitle } from 'library/components/controls/layout/SectionTitle';
import { getLinkGenerateHealthCheck } from 'library/components/link/linkUtils';
import { getLocalizedDateFormat } from 'library/formatters/DateFormatter';
import { OperationType } from 'library/models/enums/OperationType';
import AuthorizeComponent from 'main/auth/AuthorizeComponent';
import { checkUserAccess } from 'main/auth/checkUserAccess';
import { reactPlugin } from 'main/azureInsights/config';
import {
	generateHealthCheckUrl,
	healthCheckView,
} from 'main/routes/urls/RouteUrls';
import { getFilterOperators } from 'modules/EndCustomer/Shared/DataGrid/CellRender/CountryCell';
import { HealthCheckPrintPdfActionItem } from 'modules/HealthCheck/Components/Buttons/HealthCheckPrintPdfActionItem';
import { HealthCheckProtocolStatusSymbol } from 'modules/HealthCheck/Components/HealthCheckProtocolStatusSymbol';
import { getHealthCheckStatusToTranslationMapping } from 'modules/HealthCheck/Components/functions';
import { useResolvedUnitIds } from 'modules/Shared/Components/Hooks/useNumberParam';
import { LocationCountryText } from 'modules/Shared/Components/OperatingLocation/LocationCountryText';
import {
	HealthCheckFileUploader,
	HealthCheckFilesTable,
	HealthCheckTransferFiles,
} from 'modules/UnitOverview/HealthCheck';

const dataGridInitialState: GridInitialState = {
	sorting: {
		sortModel: [{ field: 'performedDate', sort: 'desc' }],
	},
	density: 'compact',
};

const HealthChecksSectionPage = (): React.JSX.Element => {
	const { t } = useTranslation([
		'healthCheck',
		'common',
		'commonButton',
		'commonUnit',
	]);
	const navigate = useNavigate();
	const unitId = useResolvedUnitIds();
	const { api } = useProductCenterApi();
	const { data } = useSuspenseQuery(
		getHealthCheckListQuery(api, unitId.publicId)
	);
	const queryClient = useQueryClient();
	const { enqueueSnackbar } = useSnackbar();
	const [deleteId, setDeleteId] = useState<number | null>(null);

	const canWriteHealthCheckFiles = checkUserAccess(queryClient, {
		areaName: AreaName.HealthCheck,
		permission: OperationType.Write,
	});
	const canAdminHealthCheckFiles = checkUserAccess(queryClient, {
		areaName: AreaName.HealthCheck,
		permission: OperationType.Delete,
	});

	const deleteMutation = useMutation({
		mutationFn: (id: number) => api.deleteHealthCheck(id),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: healthCheckKeys.list(unitId.publicId),
			});
			enqueueSnackbar(
				t('healthCheck:delete-health-check-successful-message'),
				{
					variant: 'success',
				}
			);
			setDeleteId(null);
		},
		onError: (error) => {
			enqueueSnackbar(error.message, {
				variant: 'error',
			});
		},
	});

	// open draft, if the same business partner modified it
	const previousDraft = data.find(
		(healthCheck) => healthCheck.status === 'Draft' && healthCheck.editable
	);

	const columns = useMemo((): GridColDef<HealthCheckListDto>[] => {
		const statusOptions: { value: ProtocolStatus; label: string }[] =
			Object.values(['Draft', 'Finalized'] as ProtocolStatus[]).map(
				(id) => ({
					value: id,
					label: t(getHealthCheckStatusToTranslationMapping(id)),
				})
			);
		return [
			{
				field: 'status',
				headerName: t('commonUnit:status'),
				type: 'singleSelect',
				headerAlign: 'left',
				width: 60,
				display: 'flex',
				valueOptions: statusOptions,
				renderCell: (params) => (
					<HealthCheckProtocolStatusSymbol
						status={params.row.status}
					/>
				),
			},
			{
				field: 'performedDate',
				type: 'date',
				headerName: t('healthCheck:grid-performed-date'),
				headerAlign: 'left',
				minWidth: 130,
				flex: 1,
				valueFormatter: (value) => getLocalizedDateFormat(value),
			},
			{
				field: 'performedBy',
				headerName: t('healthCheck:grid-performed-by'),
				headerAlign: 'left',
				minWidth: 150,
				flex: 2,
			},
			{
				field: 'country',
				headerName: t('common:country'),
				flex: 1,
				minWidth: 60,
				type: 'singleSelect',
				renderCell: ({ row }) => (
					<LocationCountryText
						countryCode={row.country}
						isDynamicWidth
						showCountryCode
					/>
				),
				filterOperators: getFilterOperators,
				align: 'left',
				display: 'flex',
			},
			{
				field: 'city',
				headerName: t('common:city'),
				headerAlign: 'left',
				minWidth: 130,
				flex: 1,
			},
			{
				field: 'actions',
				type: 'actions',
				align: 'right',
				minWidth: 160,
				getActions: ({ row }: GridRowParams<HealthCheckListDto>) => {
					const actions = [];

					if (
						row.editable &&
						checkUserAccess(queryClient, {
							areaName: AreaName.HealthCheck,
							permission: OperationType.Write,
						})
					) {
						actions.push(
							<Button
								variant="text"
								key={`edit-${row.id}`}
								endIcon={<ArrowForwardOutlined />}
								onClick={() => {
									navigate(
										getLinkGenerateHealthCheck(
											unitId.publicId,
											row.id,
											false
										)
									);
								}}>
								{t('commonButton:edit')}
							</Button>
						);
					} else if (row.viewable) {
						actions.push(
							<Button
								variant="text"
								key={`open-${row.id}`}
								endIcon={<ArrowForwardOutlined />}
								onClick={() => {
									navigate(
										generatePath(healthCheckView, {
											healthCheckId: row.id.toString(),
										})
									);
								}}>
								{t('commonButton:view')}
							</Button>
						);
					}

					actions.push(
						<HealthCheckPrintPdfActionItem
							key={`print-${row.id}`}
							healthCheckId={row.id}
							showInMenu
						/>
					);

					if (
						row.deletable &&
						checkUserAccess(queryClient, {
							areaName: AreaName.HealthCheck,
							permission: OperationType.Delete,
						})
					) {
						actions.push(
							<GridActionsCellItem
								key={`delete-${row.id}`}
								icon={<Trash />}
								label={t('commonButton:delete')}
								showInMenu
								onClick={() => setDeleteId(row.id)}
							/>
						);
					}

					return actions;
				},
			},
		];
	}, [t, navigate, unitId.publicId]);

	return (
		<Box>
			<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
				{previousDraft != null ? (
					<AuthorizeComponent
						areaName={AreaName.HealthCheck}
						permission={OperationType.Write}>
						<Button
							variant="outlined"
							onClick={() => {
								navigate(
									generatePath(
										generateHealthCheckUrl(unitId.publicId),
										{
											healthCheckId:
												previousDraft.id.toString(),
										}
									)
								);
							}}>
							{t('healthCheck:view-section-edit-last-button')}
						</Button>
					</AuthorizeComponent>
				) : (
					<AuthorizeComponent
						areaName={AreaName.HealthCheck}
						permission={OperationType.Create}>
						<Button
							variant="outlined"
							onClick={() => {
								navigate(
									generatePath(
										generateHealthCheckUrl(unitId.publicId)
									)
								);
							}}>
							{t('healthCheck:generate-wizard-title')}
						</Button>
					</AuthorizeComponent>
				)}
			</Box>

			<VolvoDataGrid
				isLoading={false}
				rows={data}
				columns={columns}
				initialState={dataGridInitialState}
				showToolbar
				slots={{ toolbar: DataGridToolbar }}
			/>
			<SectionTitle
				title={t('common:files-word')}
				titleVariant="h6"
				sx={{ mt: 4 }}>
				{canWriteHealthCheckFiles && (
					<Stack direction="row" spacing={1}>
						<HealthCheckTransferFiles unitId={unitId.id} />
						<HealthCheckFileUploader unitId={unitId.id} />
					</Stack>
				)}
			</SectionTitle>
			<HealthCheckFilesTable
				canWrite={canWriteHealthCheckFiles}
				canAdmin={canAdminHealthCheckFiles}
				unitId={unitId.id}
			/>
			{deleteId != null && (
				<CustomSeverityDialog
					acceptButtonLabel={t('commonButton:yes')}
					declineButtonLabel={t('commonButton:no')}
					content={t('healthCheck:confirmation-delete-health-check')}
					disabledAcceptButton={deleteMutation.isPending}
					headerText={t('commonButton:delete')}
					isOpen
					onAccept={() => deleteMutation.mutate(deleteId)}
					onDecline={() => setDeleteId(null)}
				/>
			)}
		</Box>
	);
};

export default withAITracking(
	reactPlugin,
	HealthChecksSectionPage,
	'UnitOverviewHealthChecksSectionPage',
	'application-insight-container'
);
